import { useEffect, useMemo, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { OrderList } from "@/components/OrderDialog";

function flatTree(cats) {
  const kids = {};
  cats.forEach((c) => { (kids[c.parent_id || "_root"] ||= []).push(c); });
  const ids = new Set(cats.map((c) => c.id));
  const out = [];
  const walk = (pid, depth) => (kids[pid] || []).forEach((c) => { out.push({ ...c, depth }); walk(c.id, depth + 1); });
  walk("_root", 0);
  cats.filter((c) => c.parent_id && !ids.has(c.parent_id)).forEach((c) => { out.push({ ...c, depth: 0 }); walk(c.id, 1); });
  return out;
}

export function DayEndCatalogsDialog({ onClose, onSaved }) {
  const [cats, setCats] = useState([]);
  const [sel, setSel] = useState([]);
  useEffect(() => {
    Promise.all([http.get("/catalogs"), http.get("/day-end/catalogs")]).then(([a, b]) => { setCats(a.data); setSel(b.data); });
  }, []);
  const tree = useMemo(() => flatTree(cats), [cats]);
  const byId = useMemo(() => Object.fromEntries(tree.map((c) => [c.id, c])), [tree]);
  const pathOf = (c) => { const p = []; let x = byId[c.parent_id]; while (x && p.length < 6) { p.unshift(x.name); x = byId[x.parent_id]; } return p.join(" › "); };
  const toggle = (id) => setSel(sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]);
  const save = async () => {
    try { await http.put("/day-end/catalogs", { ids: sel }); toast.success("Rapor katalogları kaydedildi"); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Kaydedilemedi"); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 max-w-3xl" data-testid="dayend-catalogs-dialog">
        <DialogHeader><DialogTitle className="font-display">Gün Sonu Rapor Katalogları</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-400 -mt-2">Raporda görünecek katalogları (üst veya alt) seçin ve sıralayın. Satış, seçili en alttaki kataloğa sayılır; satışı olmayan katalog 0 görünür. Hiç seçmezseniz ana kataloglar gösterilir.</p>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="rounded-xl border border-slate-800 max-h-[55vh] overflow-y-auto divide-y divide-slate-800/70" data-testid="dayend-catalogs-tree">
            {tree.map((c) => (
              <label key={c.id} className="flex items-center gap-2 py-2 pr-3 text-sm cursor-pointer hover:bg-slate-800/40" style={{ paddingLeft: 12 + c.depth * 16 }} data-testid={`dayend-cat-${c.id}`}>
                <Checkbox checked={sel.includes(c.id)} onCheckedChange={() => toggle(c.id)} />
                <span className="h-2 w-2 rounded-full" style={{ background: c.color || "#64748b" }} />
                <span className={c.depth ? "text-slate-300" : "font-semibold"}>{c.name}</span>
              </label>
            ))}
          </div>
          <div className="max-h-[55vh] overflow-y-auto pr-1">
            <div className="text-xs font-mono uppercase text-slate-500 mb-2">Rapor sırası ({sel.length})</div>
            {sel.length ? (
              <OrderList testId="dayend-cat-order" setItems={(items) => setSel(items.map((x) => x.id))}
                         items={sel.filter((id) => byId[id]).map((id) => ({ id, label: byId[id].name, sub: pathOf(byId[id]), onRemove: () => toggle(id) }))} />
            ) : <div className="text-sm text-slate-500 py-6 text-center">Soldan katalog seçin</div>}
          </div>
        </div>
        <Button onClick={save} className="bg-blue-600 hover:bg-blue-500" data-testid="dayend-catalogs-save">Kaydet</Button>
      </DialogContent>
    </Dialog>
  );
}
